import React, { useContext, useRef, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';

import LogInLogOutBtn from '../../modules/authentication/components/login-logout-btn';
import { EVENT, trigger } from '../../commons/utils/custom-event';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import InputWrapper from '../../commons/components/input-wrapper';
import { setPaymentMethod, currentPaymentMethod } from '../../modules/payment/slice';
import { initAnalytics } from '../../helpers/analyticsHelper';
import usePlatform from '../../commons/hooks/use-platform';
import autofillParser from '../../helpers/autofillParser';
import { currentBaseUrl } from '../../helpers/urlConst';
import { paymentIdList } from '../../helpers/constants';
import { useEmailPostApi } from '../../hooks/useAxios';
import DataContext from '../../store/dataContext';
import UiContext from '../../store/uiContext';
import { useEmailValidation } from '../../commons/hooks/use-email-validation';
import { initialErrorState } from '../../commons/utils/initial-error-state';

import './style.scss';

const EditEmail = () => {
  const {
    billingAddress1,
    billingName,
    billingPhone,
    cartId,
    device,
    email,
    isLoggedIn,
    merchantId,
    platformCartId,
    portalSettings,
    setApiEmailResponse,
    setContextBillingAddress,
    storeUrl,
    updateBillingName,
    updateContextShippingAddress,
    updateEmail,
  } = useContext(DataContext);
  const {
    setEditEmail,
    updateCheckoutState,
    TO_SHIPPING_ADDRESS_ADD,
    setAllowShippingAddressAddShowControl,
    updateLocalError,
  } = useContext(UiContext);

  const { isAuthenticationSupported } = usePlatform();
  const { isMobileDevice } = useWindowDimensions();
  const { validate } = useEmailValidation();
  const {
    apiResponse: apiEmailResponse,
    apiLoading: apiEmailResponseLoading,
    apiPostEmail,
    apiError: apiEmailError,
  } = useEmailPostApi();

  const dispatch = useDispatch();
  const selectedMethod = useSelector(currentPaymentMethod);

  const [emailValue, setEmailValue] = useState(email);
  const [oldEmail, setOldEmail] = useState(email);
  const [error, setError] = useState(initialErrorState);

  const emailInputRef = useRef(null);
  const editBtnRef = useRef(null);

  const handleEmailChange = value => {
    setEmailValue(value);
  };

  const handleKeyDown = async e => {
    if (e.key === 'Enter' || e.target.localName === 'button' || e.target.localName === 'img') {
      const { hasError, message } = await validate(emailValue);

      if (!hasError) {
        handleEmailChange(emailValue);
        apiPostEmail({ cartId, deviceId: device?.id }, emailValue);
      } else {
        setError({ hasError, message });
      }
    } else if (e.target.localName === 'div') {
      handleEmailChange(oldEmail);
      setEditEmail(false);
    }
  };

  useEffect(() => {
    setOldEmail(email);
  }, []);

  useEffect(() => {
    if (apiEmailError?.data?.success === false) {
      setError({ hasError: true, message: apiEmailError?.data?.errorMsg });
    }
  }, [apiEmailError]);

  useEffect(() => {
    if (emailInputRef) {
      emailInputRef?.current?.focus();
    }
  }, [emailInputRef]);

  useEffect(() => {
    if (apiEmailResponse) {
      initAnalytics({
        userId: apiEmailResponse?.cardTokenization?.onBehalfOf,
        merchantId,
        platformCartId,
        storeUrl,
      });
      setApiEmailResponse(apiEmailResponse);
      trigger(EVENT.EMAIL_RESPONSE_READY, apiEmailResponse);
      updateEmail(emailValue);
      const autofillInfo = autofillParser(apiEmailResponse);
      if (autofillInfo.isAvailable) {
        dispatch(setPaymentMethod(selectedMethod || paymentIdList.CREDIT_CARD));
      }
      if (autofillInfo.contextShippingAddress) {
        updateContextShippingAddress(autofillInfo.contextShippingAddress);
      }
      if (!billingAddress1 && !billingName && !billingPhone) {
        setContextBillingAddress(autofillInfo.contextBillingAddress || {});
        updateBillingName(autofillInfo.billName || '');
      }
      updateCheckoutState(TO_SHIPPING_ADDRESS_ADD);
      setAllowShippingAddressAddShowControl(autofillInfo.isAvailable);
      setEditEmail(false);
      trigger(EVENT.EDIT_EMAIL);
    }
    updateLocalError(false, false, false);
  }, [apiEmailResponse]);

  return (
    <div className="edit-email-wrapper">
      <div className="edit-wrapper">
        <InputWrapper
          classNameInput={classNames({
            'is-value': email,
            'has-error': error.hasError,
            unauthorized: !isLoggedIn && isAuthenticationSupported(),
          })}
          error={error.message}
          inputId="user-email"
          onChange={event => handleEmailChange(event.target.value)}
          onFocus={() => setError(initialErrorState)}
          onKeyDown={handleKeyDown}
          placeholder="* E-Mail"
          placeholderTop="E-Mail"
          theme={portalSettings.theme}
          type="email"
          value={emailValue}
          ref={emailInputRef}
        >
          {!isLoggedIn && isAuthenticationSupported() && !emailValue && isMobileDevice && (
            <div className="modification-input">
              <LogInLogOutBtn />
            </div>
          )}
          <button
            className={`edit-btn ${apiEmailResponseLoading && 'loading'}`}
            type="button"
            ref={editBtnRef}
            onClick={handleKeyDown}
            style={{ backgroundColor: portalSettings.theme.mainColor }}
          >
            {!apiEmailResponseLoading ? (
              <img alt="check-icon" src={`${currentBaseUrl()}assets/check.svg`} />
            ) : (
              <img alt="Loading..." src={`${currentBaseUrl()}loaders/spinner-white.png`} width={30} />
            )}
          </button>
        </InputWrapper>
      </div>
      <div className="edit-overlay" onClick={handleKeyDown} aria-hidden="true" />
    </div>
  );
};

export default EditEmail;
